import { Button } from "@studyplus/boron-ui";
import classNames from "classnames";
import * as React from "react";
import DeviceHelper from "../../../helpers/DeviceHelper";
import FileHelper from "../../../helpers/FileHelper";
import { ApiErrorInterface } from "../../../interfaces/ApiErrorResponseInterface";
import { MessageFormStateInterface } from "../../../interfaces/MessageInterface";
import StudentInterface from "../../../interfaces/StudentInterface";
import InputIcon from "../../atoms/InputIcon";
import ResumableTextarea from "../../atoms/ResumableTextarea";
import { useResumableTextarea } from "../../atoms/ResumableTextarea/useResumableTextarea";
import Textarea from "../../atoms/Textarea";
import {
  AttachmentPreviewModal,
  useAttachmentPreview,
} from "../AttachmentPreview";
import DeprecatedTagButton from "../DeprecatedTagButton";
import CommunicationStatusLabel from "./CommunicationStatusLabel";
import styles from "./MessageForm.scss";

const mimeTypes = {
  jpg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  pdf: "application/pdf",
};
type FileType = keyof typeof mimeTypes;

const resumeMessage =
  "編集途中のメッセージを復元しますか？\n（「キャンセル」を選択すると復元内容が破棄されます。）";

type Props = {
  formState?: MessageFormStateInterface;
  student?: StudentInterface;
  canSendMessage: boolean;
  allowedFileTypes: FileType[];
  disabledPlaceholder: string;
  postMessage: (content?: string, file?: File) => void;
  changeFormContent: (content: string) => void;
  changeFormFile: (file: File | null) => void;
  storageKey: string;
  textareaMaxHeightBaseElementRef?: React.RefObject<HTMLElement | null>;
};

const MessageForm = (props: Props) => {
  const { handleSubmit, isValid, apiErrors, attachmentPreview } =
    useMessageForm(props);

  return (
    <React.Fragment>
      <form className={styles.root} onSubmit={handleSubmit}>
        <ApiErrors apiErrors={apiErrors} isShow={!DeviceHelper.isPC()} />
        <div className="relative mr-4 w-full flex-col">
          {props.student && (
            <CommunicationStatusLabel
              sectionId={props.student.section.id}
              lineConnectionStatus={props.student.lineConnectionStatus}
              isGuardianEmailConfirmed={props.student.isGuardianEmailConfirmed}
              isAllowedToReceiveGuardianMessage={
                props.student.section.isAllowedToReceiveGuardianMessage
              }
            />
          )}
          <div
            className={classNames(styles.content, {
              [styles.content__withFile]: props.formState?.values.file,
              [styles.hasError]: apiErrors.length > 0,
            })}
          >
            <ContentField {...props} hasError={apiErrors.length > 0} />
            {props.canSendMessage && (
              <div className={styles.iconContainer}>
                <AttachmentIcon
                  {...props}
                  onSelectFile={attachmentPreview.selectFile}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.button}>
          <Button
            type="submit"
            disabled={props.formState?.submitting || !isValid}
            isBlock={true}
          >
            送信
          </Button>
        </div>
      </form>
      <ApiErrors apiErrors={apiErrors} isShow={DeviceHelper.isPC()} />
      <AttachmentPreviewModal
        file={attachmentPreview.previewFile}
        onConfirm={attachmentPreview.confirm}
        onCancel={attachmentPreview.cancel}
      />
    </React.Fragment>
  );
};

const useMessageForm = (props: Props) => {
  const attachmentPreview = useAttachmentPreview({
    changeFormFile: props.changeFormFile,
    destinationKey: props.storageKey,
  });

  const handleSubmit = React.useCallback(
    (e: any) => {
      e.preventDefault();

      const { formState } = props;

      if (!formState) {
        return;
      }

      attachmentPreview.clearFileError();
      props.postMessage(
        formState.values.content,
        formState.values.file ?? undefined,
      );
    },
    [
      props.formState,
      props.postMessage,
      props.postMessage,
      attachmentPreview.clearFileError,
    ],
  );

  const [isValid, setIsValid] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (!props.formState) {
      return setIsValid(false);
    }

    const { content, file } = props.formState.values;
    setIsValid(!!content || !!file);
  }, [props.formState]);

  const apiErrors = [
    ...(props.formState?.apiErrors || []),
    ...attachmentPreview.fileErrors,
  ];

  return {
    handleSubmit,
    isValid,
    apiErrors,
    attachmentPreview,
  };
};

type ApiErrorsProps = {
  apiErrors: ApiErrorInterface[] | undefined;
  isShow: boolean;
};
const ApiErrors = ({ apiErrors, isShow }: ApiErrorsProps) => {
  if (isShow && apiErrors && apiErrors.length > 0) {
    return (
      <div className={styles.errors}>
        {apiErrors.map((error: ApiErrorInterface, i: number) => {
          return (
            <p key={`MessageForm-apiErrors-${i}`} className={styles.error}>
              {error.message}
            </p>
          );
        })}
      </div>
    );
  } else {
    return null;
  }
};

type AttachmentIconProps = {
  formState?: MessageFormStateInterface;
  allowedFileTypes: FileType[];
  // 選んだファイルはプレビュー画面を通してから添付する
  onSelectFile: (file: File) => void;
};
const AttachmentIcon = (props: AttachmentIconProps) => {
  return (
    <InputIcon
      iconName="icon-picture-img"
      inputAttributes={{
        accept: props.allowedFileTypes.map((t) => mimeTypes[t]).join(","),
        id: "message_image",
        type: "file",
      }}
      tooltipLabel={`ファイルの添付( ${props.allowedFileTypes
        .map((t) => t)
        .join(",")} )`}
      onChange={handleChangeFileAttachment(props)}
      disabled={props.formState?.submitting}
    />
  );
};

const handleChangeInput = (props: Props) => (e: React.ChangeEvent<any>) => {
  props.changeFormContent(e.target.value);
};

const handleChangeFileAttachment =
  (props: AttachmentIconProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) {
      return;
    }
    props.onSelectFile(file);
  };

const handleFileDelete = (props: Props) => () => {
  props.changeFormFile(null);
};

// hasError: フォームのエラー表示欄にエラーがあるか（API のエラーと、添付のサイズ超過のエラーを合わせたもの）
const ContentField: React.FC<Props & { hasError: boolean }> = (props) => {
  const { formState, storageKey, changeFormContent, canSendMessage } = props;

  const { clearValue: clearContentStorage, ...resumableTextareaProps } =
    useResumableTextarea({
      value: formState?.values.content || "",
      key: storageKey,
      confirmMessage: resumeMessage,
      onResume: changeFormContent,
    });

  const prevSubmitting = React.useRef(formState?.submitting);

  React.useEffect(() => {
    if (formState) {
      if (
        prevSubmitting.current &&
        !formState.submitting &&
        formState.apiErrors.length === 0
      ) {
        clearContentStorage();
      }
      prevSubmitting.current = formState.submitting;
    }
  }, [formState?.submitting]);

  if (!formState) {
    return (
      <Textarea
        name="content"
        disabled={true}
        placeholder={props.disabledPlaceholder}
        value={""}
        className={styles.content__inputDisabled}
      />
    );
  }

  if (!canSendMessage) {
    return (
      <Textarea
        name="content"
        disabled={true}
        placeholder={props.disabledPlaceholder}
        value={""}
        className={styles.content__inputDisabled}
      />
    );
  }

  return (
    <>
      <ResumableTextarea
        name="content"
        hasError={props.hasError}
        placeholder="メッセージを入力"
        value={formState.values.content}
        onChange={handleChangeInput(props)}
        className={classNames(styles.content__input, {
          [styles.content__inputWithFile]: formState.values.file,
        })}
        textareaMaxHeightBaseElementRef={props.textareaMaxHeightBaseElementRef}
        {...resumableTextareaProps}
      />
      <FileName {...props} />
    </>
  );
};

const FileName = (props: Props) => {
  const file = props.formState ? props.formState.values.file : null;
  if (!file) {
    return null;
  }

  return (
    <div className="mb-2 ml-4 mt-1">
      <DeprecatedTagButton
        label={FileHelper.truncateFilename(file.name, 30)}
        iconName="icon-close-x"
        theme="lightgray"
        onClick={handleFileDelete(props)}
      />
    </div>
  );
};

export default MessageForm;
